import React, { useMemo, useCallback } from "react";
import { LuUser } from "react-icons/lu";
import {
  MdOutlineDashboard,
  MdOutlineEngineering,
  MdOutlineProductionQuantityLimits,
} from "react-icons/md";
import { GoGear } from "react-icons/go";
import { RiSurveyLine } from "react-icons/ri";
import { PiPhoneDisconnect } from "react-icons/pi";
import {
  TbFileReport
} from "react-icons/tb";
import { AiOutlineUsergroupDelete, AiOutlineEye, AiOutlineDollarCircle, AiOutlinePaperClip, AiOutlineContacts, AiOutlineOrderedList, AiOutlineFilePdf } from "react-icons/ai";
import { HiOutlineCurrencyDollar, HiOutlineCurrencyRupee } from "react-icons/hi";
import { VscTools } from "react-icons/vsc";
import { Link, useLocation } from "react-router-dom";
import { Layout, Menu, Skeleton } from "antd";
import { useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import orgConfig from "../../config/organization.json";

const { Sider } = Layout;

// Memoized icon components with consistent styling
const IconWrapper = ({ icon: Icon, size = "20px" }) => (
  <Icon style={{ fontSize: size, marginRight: "8px" }} />
);

// Common menu item creator
const createMenuItem = (label, key, icon, children = null) => ({
  label,
  key,
  icon: icon ? <IconWrapper icon={icon} /> : null,
  children,
});

// Common link creator
const createLink = (to, text, style = { textDecoration: "none" }) => (
  <Link style={style} to={to}>
    {text}
  </Link>
);

// Base menu items that are shared across different user types
const baseMenuItems = {
  dashboard: createMenuItem(
    createLink("/dashboard", "Dashboard"),
    "dashboard",
    MdOutlineDashboard
  ),
  settings: {
    label: "Settings",
    key: "settings",
    icon: <GoGear style={{ fontSize: "20px" }} />,
    children: [
      {
        label: <Link to="/settings/general">General</Link>,
        key: "general",
      },
      {
        label: <Link to="/settings/employee">Employee</Link>,
        key: "employee",
      },
      {
        label: <Link to="/settings/documentation">Documentation</Link>,
        key: "documentation",
      },
      {
        label: <Link to="/settings/systems">Systems</Link>,
        key: "systems",
      },
    ],
  },
};

// Route to menu key mapping
const routeToMenuKeyMap = {
  "/dashboard": { selected: ["dashboard"] },
  "/employees": { selected: ["employees", "activeEmployee"], open: ["employees"] },
  "/employees/inactive": { selected: ["employees", "inactiveEmployee"], open: ["employees"] },
  "/employees/salary": { selected: ["employees", "employeeSalary"], open: ["employees"] },
  "/employees/manage": { selected: ["employees", "createEmployee"], open: ["employees"] },
  "/employees/attendance": { selected: ["employees", "empAttendence"], open: ["employees"] },
  "/customers": { selected: ["customers", "activeCustomers"], open: ["customers"] },
  "/customers/manage": { selected: ["customers", "createCustomer"], open: ["customers"] },
  "/customers/inactive": { selected: ["customers", "inactiveCustomers"], open: ["customers"] },
  "/items": { selected: ["items", "itemDetails"], open: ["items"] },
  "/items/manage": { selected: ["items", "createItems"], open: ["items"] },
  "/amcs": { selected: ["amc", "amcManagement"], open: ["amc"] },
  "/amcs/manage": { selected: ["amc", "amcCreate"], open: ["amc"] },
  "/amcs/active": { selected: ["amc", "activeAMC"], open: ["amc"] },
  "/amcs/details": { selected: ["amc", "amcManagement"], open: ["amc"] },
  "/quotations": { selected: ["quotation", "quotationDetails"], open: ["quotation"] },
  "/quotations/manage": { selected: ["quotation", "createQuotation"], open: ["quotation"] },
  "/surveys": { selected: ["surveys", "assignedSurveys"], open: ["surveys"] },
  "/surveys/completed": { selected: ["surveys", "compltedSurveys"], open: ["surveys"] },
  "/visits": { selected: ["visits", "upcomingVisits"], open: ["visits"] },
  "/visits/completed": { selected: ["visits", "compltedVisits"], open: ["visits"] },
  "/attendance": { selected: ["attendance", "AttendanceDetails"], open: ["attendance"] },
  "/generatereport/formA": { selected: ["reports", "formA"], open: ["reports"] },
  "/reports/formA": { selected: ["reports", "formA"], open: ["reports"] },
  "/view/formA": { selected: ["reports", "formA"], open: ["reports"] },
  "/generatereport/formB": { selected: ["reports", "formB"], open: ["reports"] },
  "/reports/formB": { selected: ["reports", "formB"], open: ["reports"] },
  "/view/formB": { selected: ["reports", "formB"], open: ["reports"] },
  "/generatereport/trainingCertificate": { selected: ["reports", "trainingCertificate"], open: ["reports"] },
  "/reports/trainingCertificate": { selected: ["reports", "trainingCertificate"], open: ["reports"] },
  "/view/trainingCertificate": { selected: ["reports", "trainingCertificate"], open: ["reports"] },
  "/generatereport/installationCertificate": { selected: ["reports", "installationCertificate"], open: ["reports"] },
  "/reports/installationCertificate": { selected: ["reports", "installationCertificate"], open: ["reports"] },
  "/view/installationCertificate": { selected: ["reports", "installationCertificate"], open: ["reports"] },
  "/generatereport/testingCertificate": { selected: ["reports", "testCertificate"], open: ["reports"] },
  "/reports/testCertificate": { selected: ["reports", "testCertificate"], open: ["reports"] },
  "/view/testingCertificate": { selected: ["reports", "testCertificate"], open: ["reports"] },
  "/settings": { selected: ["settings"], open: ["settings"] },
  "/settings/general": { selected: ["settings", "general"], open: ["settings"] },
  "/settings/employee": { selected: ["settings", "employee"], open: ["settings"] },
  "/settings/documentation": { selected: ["settings", "documentation"], open: ["settings"] },
  "/settings/systems": { selected: ["settings", "systems"], open: ["settings"] },
  "/contact": { selected: ["contact"] },
  "/new-quotation": { selected: ["quotation", "newQuotation"], open: ["quotation"] },
  "/new-survey": { selected: ["surveys", "newSurvey"], open: ["surveys"] },
  "/new-purchaseOrder": { selected: ["purchaseOrder", "newPurchaseOrder"], open: ["purchaseOrder"] },
  "/leads": { selected: ["lead", "leadManagement"], open: ["lead"] },
  "/leads/create": { selected: ["lead", "leadCreate"], open: ["lead"] },
  "/cashLedger": { selected: ["cashLedger"], open: ["cashLedger"] },
  "/cashLedger/sell": { selected: ["cashLedger", "sellCashLedgerManagement"], open: ["cashLedger"] },
  "/cashLedger/purchase": { selected: ["cashLedger", "purchaseCashLedgerManagement"], open: ["cashLedger"] },
  "/cashLedger/manage": { selected: ["cashLedger"], open: ["cashLedger"] },
  "/licenseCopies": { selected: ["licenseCopies", "licenseCopyManagement"], open: ["licenseCopies"] },
  "/licenseCopies/manage": { selected: ["licenseCopies", "licenseCopyCreate"], open: ["licenseCopies"] },
  "/boq": { selected: ["boq", "boqManagement"], open: ["boq"] },
  "/boq/manage": { selected: ["boq", "boqCreate"], open: ["boq"] },
  "/boqItems": { selected: ["boq", "boqItemsManagement"], open: ["boq"] },
  "/boqItems/manage": { selected: ["boq", "boqItemsCreate"], open: ["boq"] },
  "/task": { selected: ["task", "taskManagement"], open: ["task"] },
  "/task/manage": { selected: ["task", "taskCreate"], open: ["task"] },
  "/purchaseOrders": { selected: ["purchaseOrder", "purchaseOrderManagement"], open: ["purchaseOrder"] },
  "/purchaseOrders/create": { selected: ["purchaseOrder", "purchaseOrderCreate"], open: ["purchaseOrder"] },
  "/brochures": { selected: ["brochures", "brochureDemo"], open: ["brochures"] },
  "/brochures/viewer": { selected: ["brochures", "brochureViewer"], open: ["brochures"] },
  "/brochures/company-overview": { selected: ["brochures", "companyOverview"], open: ["brochures"] },
  "/brochures/services": { selected: ["brochures", "servicesBrochure"], open: ["brochures"] },
  "/brochures/contact": { selected: ["brochures", "contactBrochure"], open: ["brochures"] }
};

// Menu configurations for different user types
const menuConfigs = {
  "Super Admin": [
    baseMenuItems.dashboard,
    {
      label: "Employee",
      key: "employees",
      icon: <MdOutlineEngineering style={{ fontSize: "20px" }} />,
      children: [
        createMenuItem(createLink("/employees", "Employee Management"), "activeEmployee"),
        createMenuItem(createLink("/employees/attendance", "Attendance"), "empAttendence"),
        createMenuItem(createLink("/employees/inactive", "Inactive Employee"), "inactiveEmployee"),
        createMenuItem(createLink("/employees/salary", "Salary"), "employeeSalary"),
      ],
    },
    {
      label: "Customers",
      key: "customers",
      icon: <LuUser style={{ fontSize: "20px" }} />,
      children: [
        createMenuItem(createLink("/customers", "Customer Management"), "activeCustomers"),
        createMenuItem(createLink("/customers/inactive", "Inactive Customers"), "inactiveCustomers"),
        // createMenuItem(createLink("/customers/manage", "Create Customer"), "createCustomer"),
      ],
    },
    {
      label: "Items",
      key: "items",
      icon: <MdOutlineProductionQuantityLimits style={{ fontSize: "20px" }} />,
      children: [
        createMenuItem(createLink("/items", "Item Management"), "itemDetails"),
        // createMenuItem(createLink("/items/manage", "Create Items"), "createItems"),
      ],
    },
    {
      label: "AMC",
      key: "amc",
      icon: <VscTools style={{ fontSize: "20px" }} />,
      children: [
        createMenuItem(createLink("/amcs", "AMC Management"), "amcManagement"),
        // createMenuItem(createLink("/amcs/active", "Active AMC"), "activeAMC"),
      ],
    },
    {
      label: "Quotation",
      key: "quotation",
      icon: <HiOutlineCurrencyDollar style={{ fontSize: "20px" }} />,
      children: [
        createMenuItem(createLink("/quotations/", "Quotation Management"), "quotationDetails"),
      ],
    },
    {
      label: "Surveys",
      key: "surveys",
      icon: <RiSurveyLine style={{ fontSize: "20px" }} />,
      children: [
        createMenuItem(createLink("/surveys", "Upcoming Survey"), "assignedSurveys"),
        createMenuItem(createLink("/surveys/completed", "Completed Survey"), "compltedSurveys"),
      ],
    },
    {
      label: "Reports",
      key: "reports",
      icon: <TbFileReport style={{ fontSize: "20px" }} />,
      children: [
        createMenuItem(createLink("/reports/formA", "Form A"), "formA"),
        createMenuItem(createLink("/reports/formB", "Form B"), "formB"),
        createMenuItem(createLink("/reports/trainingCertificate", "Training Certificate"), "trainingCertificate"),
        createMenuItem(createLink("/reports/installationCertificate", "Installation Report"), "installationCertificate"),
        createMenuItem(createLink("/reports/testCertificate", "Test Certificate"), "testCertificate"),
      ],
    },
    {
      label: "PO",
      key: "purchaseOrders",
      icon: <AiOutlineDollarCircle style={{ fontSize: "20px" }} />,
      children: [
        createMenuItem(createLink("/purchaseOrders", "PO Management"), "purchaseOrderManagement"),
        // createMenuItem(createLink("/purchaseOrders/create", "Create PO"), "purchaseOrderCreate"),
        // createMenuItem(createLink("/new-purchaseOrder", "New Purchase Order"), "newPurchaseOrder"),
      ],
    },
    {
      label: "BOQ",
      key: "boq",
      icon: <AiOutlinePaperClip style={{ fontSize: "20px" }} />,
      children: [
        createMenuItem(createLink("/boq", "BOQ Management"), "boqManagement"),
        createMenuItem(createLink("/boq/manage", "Create BOQ"), "boqCreate"),
        createMenuItem(createLink("/boqItems", "BOQ Items Management"), "boqItemsManagement"),
        // createMenuItem(createLink("/boqItems/manage", "Create BOQ Item"), "boqItemsCreate"),
      ],
    },
    {
      label: "Lead",
      key: "lead",
      icon: <AiOutlineContacts style={{ fontSize: "20px" }} />,
      children: [
        createMenuItem(createLink("/leads", "Lead Management"), "leadManagement"),
        // createMenuItem(createLink("/leads/create", "Create Lead"), "leadCreate"),
      ],
    },
    {
      label: "Task Diary",
      key: "task",
      icon: <AiOutlineOrderedList style={{ fontSize: "20px" }} />,
      children: [
        createMenuItem(createLink("/task", "Task Diary"), "taskManagement"),
        createMenuItem(createLink("/task/manage", "Create Task"), "taskCreate"),
      ],
    },
    {
      label: "Cash Ledger",
      key: "cashLedger",
      icon: <HiOutlineCurrencyRupee style={{ fontSize: "20px" }} />,
      children: [
        createMenuItem(createLink("/cashLedger/sell", "Sell Cash Ledger Management"), "sellCashLedgerManagement"),
        createMenuItem(createLink("/cashLedger/purchase", "Purchase Cash Ledger Management"), "purchaseCashLedgerManagement"),
        // createMenuItem(createLink("/cashLedger/manage?type=Sell", "Create Sell Cash Ledger"), "sellCashLedgerCreate"),
        // createMenuItem(createLink("/cashLedger/manage?type=Purchase", "Create Purchase Cash Ledger"), "purchaseCashLedgerCreate"),
      ],
    },
    {
      label: "License Copy",
      key: "licenseCopies",
      icon: <VscTools style={{ fontSize: "20px" }} />,
      children: [
        createMenuItem(createLink("/licenseCopies", "License Copy Management"), "licenseCopyManagement"),
        // createMenuItem(createLink("/licenseCopy/manage", "Create License Copy"), "licenseCopyCreate"),
      ],
    },
    {
      label: "Brochures",
      key: "brochures",
      icon: <AiOutlineFilePdf style={{ fontSize: "20px" }} />,
      children: [
        createMenuItem(createLink("/brochures", "Brochure Demo"), "brochureDemo"),
        createMenuItem(createLink("/brochures/viewer", "All Brochures"), "brochureViewer"),
        createMenuItem(createLink("/brochures/company-overview", "Company Overview"), "companyOverview"),
        createMenuItem(createLink("/brochures/services", "Services & Features"), "servicesBrochure"),
        createMenuItem(createLink("/brochures/contact", "Contact Information"), "contactBrochure"),
      ],
    },
    baseMenuItems.settings,
  ],
  "General Employee": [
    baseMenuItems.dashboard,
    {
      label: "Survey",
      key: "surveys",
      icon: <RiSurveyLine style={{ fontSize: "20px" }} />,
      children: [
        createMenuItem(createLink("/surveys", "Assigned Surveys"), "assignedSurveys"),
        createMenuItem(createLink("/surveys/completed", "Completed Surveys"), "compltedSurveys"),
      ],
    },
    {
      label: "AMC Visit",
      key: "visits",
      icon: <AiOutlineEye style={{ fontSize: "20px" }} />,
      children: [
        createMenuItem(createLink("/visits", "Upcoming AMC Visits"), "upcomingVisits"),
        createMenuItem(createLink("/visits/completed", "Completed AMC Visits"), "compltedVisits"),
      ],
    },
    createMenuItem(createLink("/attendance", "Attendance"), "attendance", AiOutlineUsergroupDelete),
  ],
  "Customer": [
    baseMenuItems.dashboard,
    createMenuItem(createLink("/surveys", "Survey"), "surveys", RiSurveyLine),
    {
      label: "AMC",
      key: "amc",
      icon: <VscTools style={{ fontSize: "20px" }} />,
      children: [
        createMenuItem(createLink("/amcs/active", "Active AMC"), "activeAMC"),
        createMenuItem(createLink("/amcs", "All AMC"), "amcManagement"),
      ],
    },
    createMenuItem(createLink("/reports", "Reports"), "reports", TbFileReport),
    createMenuItem(createLink("/contact", "Contact Us"), "contact", PiPhoneDisconnect),
  ],
};

const SiteSidebar = ({ active }) => {
  const { userData } = useAuth();
  const location = useLocation();
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [openKeys, setOpenKeys] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const rootSubmenuKeys = useMemo(() => [
    "employees", "customers", "items", "amc", "quotation", "purchaseOrder",
    "surveys", "visits", "attendance", "reports", "lead", "task",
    "boq", "cashLedger", "licenseCopies", "brochures", "settings"
  ], []);

  const onOpenChange = useCallback((keys) => {
    const currentOpenKeys = openKeys || [];  // Ensure we have an array even if openKeys is undefined
    const latestOpenKey = keys.find((key) => currentOpenKeys.indexOf(key) === -1);

    if (rootSubmenuKeys.indexOf(latestOpenKey) === -1) {
      setOpenKeys(keys);
    } else {
      setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    }
  }, [openKeys, rootSubmenuKeys]);

  useEffect(() => {
    // Get all path segments
    const pathSegments = location.pathname.split("/");
    
    // Try to find the closest matching route by checking paths from most specific to least specific
    let menuConfig = null;
    for (let i = pathSegments.length; i > 0; i--) {
      const testPath = pathSegments.slice(0, i).join("/");
      if (routeToMenuKeyMap[testPath]) {
        menuConfig = routeToMenuKeyMap[testPath];
        break;
      }
    }

    // If no match found, use empty config
    menuConfig = menuConfig || { selected: [], open: [] };
    
    setSelectedKeys(menuConfig.selected);
    setOpenKeys(menuConfig.open);
  }, [location.pathname]);

  useEffect(() => {
    // Don't set menu items if userData is not available yet
    if (!userData) {
      setMenuItems([]);
      setLoading(true);
      return;
    }

    const userType = userData?.employee?.accessType || "Customer";
    let items = menuConfigs[userType] || menuConfigs["Customer"];
    
    // Filter out brochures menu item if showBrochures is not set to true in localStorage
    const showBrochures = localStorage.getItem('showBrochures') === 'true';
    if (!showBrochures) {
      items = items.filter(item => item.key !== 'brochures');
    }
    
    const sidebarMenuConfig = orgConfig.organization.sidebarMenu || [];
    const sidebarMenuMap = Object.fromEntries(
      sidebarMenuConfig.map(entry => [entry.key, entry.children || null])
    );
    const filterMenu = (menuArr) => menuArr
      .map(item => {
        if (!sidebarMenuMap[item.key]) {
          if (sidebarMenuMap.hasOwnProperty(item.key)) {
            return { ...item, children: undefined };
          }
          return null;
        }
        let allowedChildren = sidebarMenuMap[item.key];
        let children = item.children
          ? item.children.filter(child => allowedChildren.includes(child.key))
          : undefined;
        return { ...item, children };
      })
      .filter(Boolean);
    items = filterMenu(items);
    setMenuItems(items);
    setLoading(false);
  }, [userData]);

  return (
    <Sider
      width={250}
      breakpoint="lg"
      collapsedWidth="0"
      style={{
        overflow: "auto",
        height: "100vh",
        position: "fixed",
        left: 0,
        top: 64,
        bottom: 0,
        background: "#fff",
        borderRight: "1px solid #f0f0f0",
        overflowY: "auto"
      }}
    >
      {loading ? (
        <Skeleton active />
      ) : (
        <Menu
          mode="inline"
          defaultSelectedKeys={active}
          style={{ 
            height: '100%', 
            borderRight: 0,
            overflowY: "auto",
            paddingBottom: '100px'
          }}
          items={menuItems}
          selectedKeys={selectedKeys}
          onOpenChange={onOpenChange}
          openKeys={openKeys}
        />
      )}
    </Sider>
  );
};

export default React.memo(SiteSidebar);
